import * as R from 'ramda';

import sportsApi from '../../utils/sportsApi';
import getGoalDescription from '../../utils/getGoalDescription';
import getPlacementName from '../../utils/getPlacementName';
import platformTypes from '../../constants/platformTypes';
import {
    processing,
    goalReached,
    onTrack,
    offTrack,
} from '../../constants/pacingStatus';

const getGoals =
    store =>
    async (
        state,
        filters = {
            statusIds: [processing, goalReached, onTrack, offTrack],
        },
    ) => {
        try {
            const userClientId = state.user.selectedUserClientId;
            const clientId = state.user.selectedClientId;

            // Sets filters as loading
            const loadingState = R.assocPath(
                ['clients', userClientId, 'pacing', 'isLoading'],
                true,
                state,
            );

            const currency = R.path(
                ['user', 'clientsById', clientId, 'currency', 'code'],
                store.getState(),
            );

            store.setState(loadingState);

            const goals = await sportsApi
                .url(`/clients/${clientId}/goals`)
                .json({
                    action: 'fetchQuery',
                    ...filters,
                })
                .post()
                .json();

            // Pacing-specific behavior: if any goal was created with league RH attribution,
            // always fetch RH data in league attribution mode so rows can resolve names/logos.
            const hasLeagueAttributionGoals = goals.some(
                g =>
                    g?.isUsingRha === true ||
                    g?.useRightsholderAttribution === true,
            );

            const [rightsholders, sponsors] = await Promise.all([
                sportsApi
                    .url(
                        `/rightsholders?includePlacements=true&includeSportId=true`,
                    )
                    .query(
                        hasLeagueAttributionGoals
                            ? { useRightsholderAttribution: true }
                            : {},
                    )
                    .get()
                    .json(),
                sportsApi.url(`/sponsors`).get().json(),
            ]);

            const updateGoals = goal => {
                const {
                    filter: {
                        rightsholderIds,
                        sponsorIds,
                        rightsholderPlacementIds,
                        platformTypeIds,
                    },
                    ...others
                } = goal;

                const rightsholderId = rightsholderIds && rightsholderIds[0];
                const sponsorId = sponsorIds && sponsorIds[0];

                const rh = rightsholders.find(rh => rh.id == rightsholderId);
                const sp = sponsors.find(sp => sp.id == sponsorId);

                const rightsholderLogoUrl = R.path(['mainLogo', 'url'], rh);
                const sponsorLogoUrl = R.path(['logos', 0, 'url'], sp);

                const placementNames =
                    rightsholderPlacementIds?.length !== 0 && rh
                        ? getPlacementName(rightsholderPlacementIds, rh)
                        : [];
                const platformType = (platformTypeIds || [])
                    .filter((value, index, self) => {
                        return self.indexOf(value) === index;
                    })
                    .sort()
                    .reduce((acc, id, index) => {
                        if (index === 0) {
                            acc += platformTypes[`${id}`];
                        } else {
                            acc += `, ${platformTypes[`${id}`]}`;
                        }
                        return acc;
                    }, '');

                const description = getGoalDescription({
                    currency,
                    goal,
                });

                return {
                    rightsholderId,
                    rightsholderLogoUrl,
                    rightsholderName:
                        rh?.name ||
                        others?.rightsholderName ||
                        `${rightsholderId || ''}`,
                    sponsorId,
                    sponsorLogoUrl,
                    sponsorName:
                        sp?.name || others?.sponsorName || `${sponsorId || ''}`,
                    rightsholderPlacementIds,
                    placementNames: placementNames.filter(Boolean),
                    platformType,
                    description,
                    ...others,
                };
            };

            const updatedGoals = goals.map(updateGoals);

            store.setState(
                R.evolve(
                    {
                        clients: {
                            [userClientId]: {
                                pacing: {
                                    isLoading: R.F,
                                    goals: R.always(updatedGoals),
                                },
                            },
                        },
                    },
                    store.getState(),
                ),
                true,
            );
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    };

export default getGoals;
